import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CalendarDays, ListOrdered, Loader2, AlertCircle, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import FAQLink from '../faqs/FAQLink';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const WINDOW_CHOICES = [3, 6, 9, 12];

const EVENT_LABELS = {
  bond_coupon: 'Bond coupon',
  tbill_maturity: 'T-bill matures',
  fd_maturity: 'FD matures',
  dividend: 'Dividend',
  distribution: 'Distribution',
  convertible_maturity: 'Convertible matures',
  safe_maturity: 'SAFE matures',
};

const UNCERTAIN_OUTCOME_TYPES = new Set(['convertible_maturity', 'safe_maturity']);

function fmtMoney(v, cur) {
  if (v == null) return '—';
  try {
    return `${cur ? cur + ' ' : ''}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } catch {
    return `${v}`;
  }
}

function fmtAmountOrOutcome(event) {
  if (UNCERTAIN_OUTCOME_TYPES.has(event.event_type)) return 'Outcome uncertain';
  return fmtMoney(event.gross_amount, event.currency);
}

function monthLabel(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

function CashflowCalendarCard({ user, refreshUser }) {
  const initialMonths = WINDOW_CHOICES.includes(user?.cashflow_window_months)
    ? user.cashflow_window_months
    : 12;

  const [months, setMonths] = useState(initialMonths);
  const [view, setView] = useState('calendar'); // 'calendar' | 'list'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchCalendar = useCallback(async (m) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/cashflow/calendar?months=${m}`, { headers: authHeaders() });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `Request failed (${resp.status})`);
      }
      setData(await resp.json());
    } catch (e) {
      setError(e.message || 'Failed to load cashflow calendar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalendar(months);
  }, [months, fetchCalendar]);

  const persistWindow = async (m) => {
    setMonths(m);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/users/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ cashflow_window_months: m }),
      });
      if (resp.ok && refreshUser) refreshUser();
    } catch {
      toast.error('Could not save your window preference');
    }
  };

  const monthsList = data?.summary?.by_month || [];
  const events = data?.events || [];
  const confidence = data?.summary?.confidence_breakdown || { high: 0, low: 0 };
  const eventsByMonth = useMemo(() => {
    const map = {};
    for (const e of events) {
      const ym = (e.event_date || '').slice(0, 7);
      if (!ym) continue;
      (map[ym] = map[ym] || []).push(e);
    }
    return map;
  }, [events]);

  const totalCount = data?.summary?.total_count ?? 0;

  return (
    <Card className="portfolio-card" data-testid="cashflow-calendar-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-emerald-700 shrink-0" />
            <CardTitle className="text-base sm:text-xl font-serif">Cashflow Calendar</CardTitle>
            <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 text-xs">
              Next {months} months
            </Badge>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex border border-slate-300 rounded-md overflow-hidden text-xs" data-testid="cashflow-window-toggle">
              {WINDOW_CHOICES.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => persistWindow(m)}
                  className={`px-2.5 py-1 ${months === m ? 'bg-emerald-700 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                  data-testid={`cashflow-window-${m}m`}
                >
                  {m}m
                </button>
              ))}
            </div>
            <div className="inline-flex border border-slate-300 rounded-md overflow-hidden text-xs" data-testid="cashflow-view-toggle">
              <button
                type="button"
                onClick={() => setView('calendar')}
                className={`px-2.5 py-1 flex items-center gap-1 ${view === 'calendar' ? 'bg-emerald-700 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                data-testid="cashflow-view-calendar"
              >
                <CalendarDays className="w-3.5 h-3.5" /> Calendar
              </button>
              <button
                type="button"
                onClick={() => setView('list')}
                className={`px-2.5 py-1 flex items-center gap-1 ${view === 'list' ? 'bg-emerald-700 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                data-testid="cashflow-view-list"
              >
                <ListOrdered className="w-3.5 h-3.5" /> List
              </button>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading projected cashflows…
          </div>
        ) : error ? (
          <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="cashflow-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
          </div>
        ) : totalCount === 0 ? (
          <div className="flex items-start gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-3" data-testid="cashflow-empty">
            <Info className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
            <span>
              No projected cashflows in the next {months} months. Bond coupons, T-bill / FD maturities, and dividend cadence will appear here once you have holdings with future cashflows.
            </span>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600 mb-3" data-testid="cashflow-summary-band">
              <span className="font-medium text-slate-900">{totalCount} projected event{totalCount === 1 ? '' : 's'}</span>
              {confidence.low > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-amber-800">
                  <AlertCircle className="w-3 h-3" /> {confidence.low} unconfirmed projection{confidence.low === 1 ? '' : 's'}
                </span>
              )}
              <span className="text-slate-500">
                {Object.entries(data?.summary?.by_currency || {})
                  .map(([cur, amt]) => `${cur} ${Number(amt).toLocaleString(undefined, { maximumFractionDigits: 0 })}`)
                  .join(' · ')}
              </span>
            </div>

            {view === 'calendar' ? (
              <MonthGridView monthsList={monthsList} eventsByMonth={eventsByMonth} />
            ) : (
              <EventListView events={events} />
            )}

            <p className="mt-3 text-[11px] text-slate-500 italic">
              Reconciliation lens — derived from your recorded holdings. Amounts in native currency, no FX conversion.
              Dividend / distribution cadence is inferred from past receipts; rows tagged &quot;Unconfirmed&quot; are based on
              only two past observations and may be off. Convertible / SAFE maturity rows show the date only — the cash
              outcome depends on the terms (equity conversion vs. cash repayment).
            </p>
          </>
        )}
        {!loading && (
          <div className="mt-3">
            <FAQLink categoryKey="cashflow" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MonthGridView({ monthsList, eventsByMonth }) {
  const [expanded, setExpanded] = useState(null); // ym string
  if (!monthsList || monthsList.length === 0) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="cashflow-month-grid">
      {monthsList.map((m) => {
        const isOpen = expanded === m.month;
        const evs = eventsByMonth[m.month] || [];
        const hasLow = evs.some((e) => e.confidence === 'low');
        return (
          <button
            key={m.month}
            type="button"
            onClick={() => setExpanded(isOpen ? null : m.month)}
            className={`text-left rounded-md border px-3 py-2 transition ${isOpen ? 'border-emerald-500 bg-emerald-50/40' : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50'}`}
            data-testid={`cashflow-month-cell-${m.month}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-800">{monthLabel(m.month)}</span>
              <Badge variant="outline" className="text-[10px] bg-white">
                {m.count}
              </Badge>
            </div>
            <div className="mt-1 text-[11px] text-slate-500 truncate">
              {Object.entries(m.by_currency).map(([cur, v]) => `${cur} ${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`).join(' · ')}
            </div>
            {hasLow && (
              <div className="mt-1 text-[10px] text-amber-700">Includes unconfirmed</div>
            )}
            {isOpen && evs.length > 0 && (
              <div className="mt-2 space-y-1 max-h-40 overflow-auto" data-testid={`cashflow-month-expanded-${m.month}`}>
                {evs.map((e, i) => (
                  <div key={`${m.month}-${i}`} className="text-[11px] text-slate-700 border-t border-slate-100 pt-1">
                    <span className="font-medium">{e.event_date}</span>
                    {' · '}
                    <span className="text-slate-500">{EVENT_LABELS[e.event_type] || e.event_type}</span>
                    {' · '}
                    <span>{fmtAmountOrOutcome(e)}</span>
                    {e.confidence === 'low' && (
                      <span className="ml-1 inline-block text-amber-700 text-[10px]">(unconfirmed)</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

function EventListView({ events }) {
  if (!events || events.length === 0) return null;
  return (
    <div className="space-y-1.5" data-testid="cashflow-event-list">
      {events.map((e, i) => (
        <div
          key={`${e.investment_id || 'x'}-${e.event_date}-${i}`}
          className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 rounded-md border ${e.confidence === 'low' ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200 bg-white'}`}
          data-testid={`cashflow-event-row-${i}`}
        >
          <div className="min-w-0 flex-1">
            <div className="text-xs text-slate-900">
              <span className="font-semibold">{e.event_date}</span>
              {' · '}
              <span className="text-slate-700">{EVENT_LABELS[e.event_type] || e.event_type}</span>
              {' · '}
              <span className="font-medium">{e.investment_name || '—'}</span>
              {e.counterparty && <span className="text-slate-500"> · {e.counterparty}</span>}
            </div>
            {e.notes && <div className="text-[11px] text-slate-500 mt-0.5">{e.notes}</div>}
          </div>
          <div className="text-xs text-right shrink-0">
            <div className="font-medium text-slate-900">{fmtAmountOrOutcome(e)}</div>
            {e.confidence === 'low' && (
              <div className="text-[10px] text-amber-700">Unconfirmed</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default CashflowCalendarCard;
